import React, { useState, useEffect, useMemo } from "react";
import { Flex, Text } from "@jsq/ds/core";
import { SubmitButton, FormRadioGroup, FormRadioButton, useFormContext } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { InstitutionAccountTypeEnum } from "../../../graphql/supergraphEnums";
import { type AccountTypeEnum, type ProfileType } from "../../../graphql/supergraphTypes";
import {
  getAllSupportedAccountTypes,
  extendedEntityAccountTypes,
} from "../../../utils/account_types";
import { DefaultModal } from "../../common/DefaultModal";
import { provideProfileName } from "../../ProfileTree/utils";
import { getAllAffectedDescendantProfiles, isCategoryChanged } from "../utils";
import { extendedTypesText } from "./styles_vanilla.css";

export type AccountTypeFormContentProps = {
  accountName: string;
  isLoading: boolean;
  isControllerMode: boolean;
  onExtendedTypesToggle: (showExtendedTypes: boolean) => void;
  profiles?: ProfileType[];
  profileId?: string;
};

export const AccountTypeFormContent = ({
  accountName,
  isLoading,
  isControllerMode,
  onExtendedTypesToggle,
  profiles = [],
  profileId = "",
}: AccountTypeFormContentProps) => {
  const [showExtendedTypes, setShowExtendedTypes] = useState(false);
  const [pendingAccountType, setPendingAccountType] = useState<AccountTypeEnum | null>(null);
  const {
    values,
    errors,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    submitCount,
    touched,
    submitForm,
    validateForm,
    initialValues,
  } = useFormContext<{ accountType: AccountTypeEnum | undefined }>();

  const currentAccountType = initialValues.accountType;

  const affectedChildProfiles = useMemo(
    () => getAllAffectedDescendantProfiles(profiles, profileId),
    [profiles, profileId],
  );

  useEffect(() => {
    if (submitCount > 0 && !touched.accountType) {
      void setFieldTouched("accountType", true);
    }
  }, [submitCount, touched.accountType, setFieldTouched]);

  useEffect(() => {
    if (values.accountType && errors.accountType) {
      setFieldError("accountType", undefined);
    }
  }, [values.accountType, errors.accountType, setFieldError]);

  const handleToggleExtendedTypes = () => {
    const newShowExtendedTypes = !showExtendedTypes;
    setShowExtendedTypes(newShowExtendedTypes);
    onExtendedTypesToggle(newShowExtendedTypes);

    if (!newShowExtendedTypes && values.accountType) {
      const isExtendedType = extendedEntityAccountTypes.has(
        values.accountType as InstitutionAccountTypeEnum,
      );
      if (isExtendedType) {
        void setFieldValue("accountType", undefined, false);
        void setFieldTouched("accountType", false, false);
        setFieldError("accountType", undefined);
      }
    }
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    void (async () => {
      const validationErrors = await validateForm();
      if (Object.keys(validationErrors).length > 0) {
        void setFieldTouched("accountType", true);
        return;
      }

      const selectedAccountType = values.accountType;
      if (!selectedAccountType) {
        void setFieldTouched("accountType", true);
        return;
      }

      if (isCategoryChanged(currentAccountType, selectedAccountType)) {
        setPendingAccountType(selectedAccountType);
      } else {
        void submitForm();
      }
    })();
  };

  const handleConfirmChange = () => {
    void submitForm();
    setPendingAccountType(null);
  };

  const handleCancelChange = () => {
    setPendingAccountType(null);
  };

  return (
    <>
      <FormRadioGroup
        name="accountType"
        label={
          accountName
            ? `What type of account is ${accountName}?`
            : `What type of account is this ${isControllerMode ? "controller" : "owner"}?`
        }
      >
        {getAllSupportedAccountTypes(showExtendedTypes, isControllerMode).map((item) => (
          <FormRadioButton key={item.value} value={item.value}>
            {item.label}
          </FormRadioButton>
        ))}
      </FormRadioGroup>

      <Text className={extendedTypesText} onClick={handleToggleExtendedTypes}>
        {showExtendedTypes ? "Hide additional entity types" : "See additional entity types"}
      </Text>

      <Flex justifyContent="flex-end" gap={Sizes.MD}>
        <SubmitButton isLoading={isLoading} onClick={handleButtonClick}>
          Continue
        </SubmitButton>
      </Flex>

      <DefaultModal
        show={pendingAccountType != null}
        title="Warning"
        body={
          <Flex flexDirection="column" gap={Sizes.MD}>
            {affectedChildProfiles.length > 0 ? (
              <>
                <Text>
                  Changing {accountName}&apos;s account type will remove all previously
                  submitted data, documents and its subsidiaries:
                </Text>
                <ul>
                  {affectedChildProfiles.map((profile) => (
                    <li key={profile.id}>{provideProfileName(profile)}</li>
                  ))}
                </ul>
              </>
            ) : (
              <Text>
                Changing {accountName}&apos;s account type will remove all previously submitted
                data and documents.
              </Text>
            )}
          </Flex>
        }
        posBtnText="Continue"
        posBtnTestId="warning-modal-continue-button"
        negBtnText="Cancel"
        posBtnVariant="danger"
        onClose={handleCancelChange}
        onConfirm={handleConfirmChange}
      />
    </>
  );
};
